/**
 * Cargas de vendedor e acerto: lista e abertura de carga. Cada carga tem página própria
 * (/cargas/:id, `CargaPage`) com o acerto em três etapas — informar o retorno, confirmar a
 * conferência e finalizar (nunca pelo vendedor da carga).
 */
import {
  LOAD_SETTLEMENT_SUMMARY_LABEL,
  LOAD_STATUS_LABEL,
  formatMoney,
  loadCreateSchema,
  type Load,
  type LoadDetail,
  type LoadSettlementSummary,
  type LoadStatus,
  type Page,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useCan } from "@/app/me";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { apiFetch } from "@/lib/api";
import { buttonClasses } from "@cafegestor/ui";
import { useCadastroOptions } from "../cadastros/lib";
import { Pager, SaveError, SearchInput } from "../cadastros/widgets";
import { dateTimeBR, useDocAction } from "../estoque/lib";
import { WarehouseSelect } from "../estoque/pickers";
import { ORDER_KEY } from "../vendas/lib";
import { LOAD_KEY } from "./lib";

const INVALIDATE = [LOAD_KEY, ["estoque"], ORDER_KEY];
const LOAD_TONE: Record<LoadStatus, "neutral" | "ok" | "warn" | "error"> = {
  aberta: "warn",
  acertada: "ok",
  cancelada: "neutral",
};
const money = (v: string | null | undefined) =>
  v === null || v === undefined ? "—" : formatMoney(v);
const SUMMARY_TONE: Record<LoadSettlementSummary, "neutral" | "ok" | "warn" | "info"> = {
  nao_iniciado: "neutral",
  prestacao_enviada: "info",
  retorno_informado: "warn",
  conferido: "info",
  finalizado: "ok",
  prestacao_desatualizada: "warn",
};

export function CargasPage() {
  const can = useCan();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState<LoadStatus | "todas">("todas");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const params = new URLSearchParams({ status, limit: "50", offset: String(offset) });
  if (q.trim()) params.set("q", q.trim());
  const list = useQuery({
    queryKey: [...LOAD_KEY, "lista", params.toString()],
    queryFn: () => apiFetch<Page<Load>>(`/cargas?${params.toString()}`),
    placeholderData: (p) => p,
  });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Cargas e acertos"
        description="A carga leva mercadoria da fábrica para o estoque do vendedor (uma carga aberta por vendedor, com quantos carregamentos forem precisos). No acerto, o que não foi vendido volta com os mesmos lotes; falta, sobra e diferença de dinheiro ficam registradas, com liberação e motivo."
        actions={
          can("cargas.carga.criar") && (
            <Button onClick={() => setCreating(true)} disabled={creating}>
              Nova carga
            </Button>
          )
        }
      />
      {creating && (
        <NewLoad
          onClose={() => setCreating(false)}
          onDone={(id) => {
            setCreating(false);
            void navigate({ to: "/cargas/$id", params: { id } });
          }}
        />
      )}
      <Card>
        <div className="mb-4 flex flex-wrap gap-3">
          <SearchInput
            placeholder="Buscar número ou vendedor"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
          <Select
            className="max-w-xs"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as LoadStatus | "todas");
              setOffset(0);
            }}
          >
            <option value="todas">Todas</option>
            {(Object.keys(LOAD_STATUS_LABEL) as LoadStatus[]).map((s) => (
              <option key={s} value={s}>
                {LOAD_STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
        </div>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table
              head={[
                "Número",
                "Vendedor",
                "Aberta em",
                "Carregamentos",
                "Vendas",
                "Situação",
                "Acerto",
                " ",
              ]}
            >
              {list.data?.items.map((l) => (
                <tr key={l.id}>
                  <td className="px-3 py-2 font-mono">{l.number}</td>
                  <td className="px-3 py-2">{l.salespersonName}</td>
                  <td className="px-3 py-2">{dateTimeBR(l.openedAt)}</td>
                  <td className="px-3 py-2">{l.shipmentCount}</td>
                  <td className="px-3 py-2">
                    {l.orderCount} · {money(l.salesTotal)}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={LOAD_TONE[l.status]}>{LOAD_STATUS_LABEL[l.status]}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    {l.status === "cancelada" ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <Badge tone={SUMMARY_TONE[l.settlementSummary]}>
                        {LOAD_SETTLEMENT_SUMMARY_LABEL[l.settlementSummary]}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to="/cargas/$id"
                      params={{ id: l.id }}
                      className={buttonClasses(
                        l.status === "aberta" && l.orderCount > 0 ? "secondary" : "ghost",
                        "sm",
                      )}
                    >
                      {l.status === "aberta" && l.orderCount > 0 ? "Fazer acerto" : "Abrir"}
                    </Link>
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function NewLoad({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const options = useCadastroOptions();
  const [salespersonId, setSalesperson] = useState("");
  const [originWarehouseId, setOrigin] = useState("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const create = useDocAction<unknown, LoadDetail>({
    path: () => "/cargas",
    invalidate: INVALIDATE,
    onDone: (l) => onDone(l.id),
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = loadCreateSchema.safeParse({ salespersonId, originWarehouseId, notes });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    create.mutate(parsed.data);
  }
  return (
    <Card title="Nova carga">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Vendedor"
            hint="O estoque dele precisa estar zerado (acerto anterior fechado)"
          >
            <Select value={salespersonId} onChange={(e) => setSalesperson(e.target.value)}>
              <option value="">Selecione</option>
              {options.data?.salespeople.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code ? `${s.code} — ${s.name}` : s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Sai de" hint="Vazio: depósito padrão da fábrica">
            <WarehouseSelect
              value={originWarehouseId}
              onChange={setOrigin}
              allOption="Padrão (fábrica)"
              excludeVendor
            />
          </Field>
          <Field label="Observações">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={create.isPending}>
            Abrir carga
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
        {formError && <Alert tone="error">{formError}</Alert>}
        <SaveError error={create.error} />
      </form>
    </Card>
  );
}
